import { makeObservable } from '@zephyr3d/base';
import { PBPrimitiveTypeInfo, PBPrimitiveType, BaseDevice, isIntegerTextureFormat, isSignedTextureFormat, getTextureFormatBlockSize, isCompressedTextureFormat, hasDepthChannel, hasRedChannel, hasGreenChannel, hasBlueChannel, hasAlphaChannel, isFloatTextureFormat } from '@zephyr3d/device';
import { isWebGL2, WebGLError } from './utils.js';
import { WebGLEnum } from './webgl_enum.js';
import { WebGLTexture2D } from './texture2d_webgl.js';
import { WebGLTexture2DArray } from './texture2darray_webgl.js';
import { WebGLTexture3D } from './texture3d_webgl.js';
import { WebGLTextureCube } from './texturecube_webgl.js';
import { WebGLTextureVideo } from './texturevideo_webgl.js';
import { WebGLVertexLayout } from './vertexlayout_webgl.js';
import { WebGLGPUBuffer } from './buffer_webgl.js';
import { WebGLIndexBuffer } from './indexbuffer_webgl.js';
import { WebGLFrameBuffer } from './framebuffer_webgl.js';
import { WebGLDepthState, WebGLRenderStateSet, WebGLBlendingState, WebGLColorState, WebGLRasterizerState, WebGLStencilState } from './renderstate_webgl.js';
import { GPUTimer } from './gpu_timer.js';
import { WebGLMiscCaps, WebGLFramebufferCaps, WebGLShaderCaps, WebGLTextureCaps } from './capabilities_webgl.js';
import { WebGLBindGroup } from './bindgroup_webgl.js';
import { WebGLGPUProgram } from './gpuprogram_webgl.js';
import { textureWrappingMap, textureMagFilterToWebGL, textureMinFilterToWebGL, primitiveTypeMap, typeMap } from './constants_webgl.js';
import { SamplerCache } from './sampler_cache.js';
import { WebGLStructuredBuffer } from './structuredbuffer_webgl.js';

const typeU16 = PBPrimitiveTypeInfo.getCachedTypeInfo(PBPrimitiveType.U16);
const tempInt32Array = new Int32Array(4);
const tempUint32Array = new Uint32Array(4);
class WebGLDevice extends BaseDevice {
    _context;
    _isWebGL2;
    _msaaSampleCount;
    _loseContextExtension;
    _contextLost;
    _isRendering;
    _reverseWindingOrder;
    _deviceCaps;
    _vaoExt;
    _instancedArraysExt;
    _drawBuffersExt;
    _currentProgram;
    _currentVertexData;
    _currentStateSet;
    _currentBindGroups;
    _currentBindGroupOffsets;
    _currentViewport;
    _currentScissorRect;
    _samplerCache;
    _textureSamplerMap;
    _captureRenderBundle;
    _deviceUniformBuffers;
    _deviceUniformBufferOffsets;
    _bindTextures;
    _bindSamplers;
    _readFormats;
    _adapterInfo;
    constructor(backend, cvs, options){
        super(cvs, backend, options?.dpr);
        this._isRendering = false;
        this._captureRenderBundle = null;
        this._msaaSampleCount = options?.msaa ? 4 : 1;
        const context = this.canvas.getContext(backend === backend1 ? 'webgl' : 'webgl2', {
            antialias: !!options?.msaa,
            depth: true,
            stencil: true,
            premultipliedAlpha: false
        });
        if (!context) {
            throw new Error('Invalid argument or no webgl support');
        }
        this._isWebGL2 = isWebGL2(context);
        this._adapterInfo = {
            vendor: context.getParameter(context.VENDOR),
            renderer: context.getParameter(context.RENDERER),
            version: context.getParameter(context.VERSION)
        };
        this._contextLost = false;
        this._vaoExt = null;
        this._instancedArraysExt = null;
        this._drawBuffersExt = null;
        this._reverseWindingOrder = false;
        this._context = context;
        this._currentProgram = null;
        this._currentVertexData = null;
        this._currentStateSet = null;
        this._currentBindGroups = [];
        this._currentBindGroupOffsets = [];
        this._currentViewport = {
            x: 0,
            y: 0,
            width: this.deviceXToScreen(this.canvas.width),
            height: this.deviceYToScreen(this.canvas.height),
            default: true
        };
        this._currentScissorRect = {
            x: 0,
            y: 0,
            width: this.deviceXToScreen(this.canvas.width),
            height: this.deviceYToScreen(this.canvas.height),
            default: true
        };
        this._deviceUniformBuffers = [];
        this._deviceUniformBufferOffsets = [];
        this._bindTextures = {
            [WebGLEnum.TEXTURE_2D]: [],
            [WebGLEnum.TEXTURE_CUBE_MAP]: [],
            [WebGLEnum.TEXTURE_3D]: [],
            [WebGLEnum.TEXTURE_2D_ARRAY]: []
        };
        this._bindSamplers = [];
        this._samplerCache = new SamplerCache(this);
        this._textureSamplerMap = new WeakMap();
        this._loseContextExtension = this._context.getExtension('WEBGL_lose_context');
        this._readFormats = {};
        this.canvas.addEventListener('webglcontextlost', (evt)=>{
            this._contextLost = true;
            evt.preventDefault();
            this.handleContextLost();
        }, false);
        this.canvas.addEventListener('webglcontextrestored', ()=>{
            this._contextLost = false;
            this.handleContextRestored();
        }, false);
    }
    get context() {
        return this._context;
    }
    getAdapterInfo() {
        return this._adapterInfo;
    }
    getFrameBufferSampleCount() {
        return this.getFramebuffer()?.getSampleCount() ?? this._msaaSampleCount;
    }
    get isWebGL2() {
        return this._isWebGL2;
    }
    get drawingBufferWidth() {
        return this.getDrawingBufferWidth();
    }
    get drawingBufferHeight() {
        return this.getDrawingBufferHeight();
    }
    get clientWidth() {
        return this.canvas.clientWidth;
    }
    get clientHeight() {
        return this.canvas.clientHeight;
    }
    isContextLost() {
        return this._contextLost;
    }
    getDeviceCaps() {
        return this._deviceCaps;
    }
    get vaoExt() {
        return this._vaoExt;
    }
    get instancedArraysExt() {
        return this._instancedArraysExt;
    }
    get drawBuffersExt() {
        return this._drawBuffersExt;
    }
    getDrawingBufferWidth() {
        return this._context._currentFramebuffer?.getWidth() || this._context.drawingBufferWidth;
    }
    getDrawingBufferHeight() {
        return this._context._currentFramebuffer?.getHeight() || this._context.drawingBufferHeight;
    }
    getBackBufferWidth() {
        return this.canvas.width;
    }
    getBackBufferHeight() {
        return this.canvas.height;
    }
    invalidateBindingTextures() {
        this._bindTextures = {
            [WebGLEnum.TEXTURE_2D]: [],
            [WebGLEnum.TEXTURE_CUBE_MAP]: [],
            [WebGLEnum.TEXTURE_3D]: [],
            [WebGLEnum.TEXTURE_2D_ARRAY]: []
        };
    }
    bindTexture(target, layer, texture, sampler) {
        const tex = texture?.object ?? null;
        const gl = this._context;
        gl.activeTexture(WebGLEnum.TEXTURE0 + layer);
        if (this._bindTextures[target][layer] !== tex) {
            gl.bindTexture(target, tex);
            this._bindTextures[target][layer] = tex;
        }
        if (this._isWebGL2) {
            const samp = sampler?.object ?? null;
            if (samp && this._bindSamplers[layer] !== samp) {
                gl.bindSampler(layer, samp);
                this._bindSamplers[layer] = samp;
            }
        } else if (texture && sampler && this._textureSamplerMap.get(texture) !== sampler) {
            const fallback = texture.isWebGL1Fallback;
            this._textureSamplerMap.set(texture, sampler);
            gl.texParameteri(target, WebGLEnum.TEXTURE_WRAP_S, textureWrappingMap[sampler.addressModeU]);
            gl.texParameteri(target, WebGLEnum.TEXTURE_WRAP_T, textureWrappingMap[sampler.addressModeV]);
            gl.texParameteri(target, WebGLEnum.TEXTURE_MAG_FILTER, textureMagFilterToWebGL(sampler.magFilter));
            gl.texParameteri(target, WebGLEnum.TEXTURE_MIN_FILTER, textureMinFilterToWebGL(sampler.minFilter, fallback ? 'none' : sampler.mipFilter));
            if (this.getDeviceCaps().textureCaps.supportAnisotropicFiltering) {
                gl.texParameterf(target, WebGLEnum.TEXTURE_MAX_ANISOTROPY, sampler.maxAnisotropy);
            }
        }
    }
    bindUniformBuffer(index, buffer, offset) {
        if (this._deviceUniformBuffers[index] !== buffer.object || this._deviceUniformBufferOffsets[index] !== offset) {
            if (offset) {
                this.context.bindBufferRange(WebGLEnum.UNIFORM_BUFFER, index, buffer.object, offset, buffer.byteLength - offset);
            } else {
                this.context.bindBufferBase(WebGLEnum.UNIFORM_BUFFER, index, buffer.object);
            }
            this._deviceUniformBuffers[index] = buffer.object;
            this._deviceUniformBufferOffsets[index] = offset;
        }
    }
    async initContext() {
        this.initContextState();
        await this.initResizer();
    /*
    this.on('resize', () => {
      const width = Math.max(1, Math.round(this.canvas.clientWidth * this._dpr));
      const height = Math.max(1, Math.round(this.canvas.clientHeight * this._dpr));
      if (width !== this.canvas.width || height !== this.canvas.height) {
        this.canvas.width = width;
        this.canvas.height = height;
        this.setViewport(this._currentViewport);
        this.setScissor(this._currentScissorRect);
      }
    });
    this.dispatchEvent('resize', this.canvas.clientWidth, this.canvas.clientHeight);
    */ }
    _handleResize(_cssWidth, _cssHeight, deviceWidth, deviceHeight) {
        const lastWidth = this.canvas.clientWidth;
        const lastHeight = this.canvas.clientHeight;
        this.canvas.width = deviceWidth;
        this.canvas.height = deviceHeight;
        if (this.canvas.clientWidth !== lastWidth && this.canvas.clientWidth === this.canvas.width || this.canvas.clientHeight !== lastHeight && this.canvas.clientHeight === this.canvas.height) {
            console.warn('[Engine] Canvas intrinsic size affected layout. CSS size locked to prevent feedback loop.');
            this.canvas.style.width = `${lastWidth}px`;
            this.canvas.style.height = `${lastHeight}px`;
        }
        this.setViewport(this._currentViewport);
        this.setScissor(this._currentScissorRect);
    }
    clearFrameBuffer(clearColor, clearDepth, clearStencil) {
        const gl = this._context;
        const colorFlag = clearColor ? gl.COLOR_BUFFER_BIT : 0;
        const depthFlag = typeof clearDepth === 'number' ? gl.DEPTH_BUFFER_BIT : 0;
        const stencilFlag = typeof clearStencil === 'number' ? gl.STENCIL_BUFFER_BIT : 0;
        if (colorFlag || depthFlag || stencilFlag) {
            WebGLDepthState.applyDefaults(this._context);
            if (isWebGL2(gl) && gl._currentFramebuffer) {
                if (depthFlag || stencilFlag) {
                    const depthAttachment = gl._currentFramebuffer.getDepthAttachment();
                    if (depthAttachment) {
                        gl.clearBufferfi(WebGLEnum.DEPTH_STENCIL, 0, clearDepth ?? 1, clearStencil ?? 0);
                    }
                }
                if (clearColor) {
                    const attachments = gl._currentFramebuffer.getColorAttachments();
                    for(let i = 0; i < attachments.length; i++){
                        if (isIntegerTextureFormat(attachments[i].format)) {
                            if (isSignedTextureFormat(attachments[i].format)) {
                                tempInt32Array[0] = clearColor[0];
                                tempInt32Array[1] = clearColor[1];
                                tempInt32Array[2] = clearColor[2];
                                tempInt32Array[3] = clearColor[3];
                                gl.clearBufferiv(WebGLEnum.COLOR, i, tempInt32Array);
                            } else {
                                tempUint32Array[0] = clearColor[0];
                                tempUint32Array[1] = clearColor[1];
                                tempUint32Array[2] = clearColor[2];
                                tempUint32Array[3] = clearColor[3];
                                gl.clearBufferuiv(WebGLEnum.COLOR, i, tempUint32Array);
                            }
                        } else {
                            gl.clearBufferfv(WebGLEnum.COLOR, i, clearColor);
                        }
                    }
                }
            } else {
                if (clearColor) {
                    gl.clearColor(clearColor[0], clearColor[1], clearColor[2], clearColor[3]);
                }
                if (depthFlag) {
                    gl.clearDepth(clearDepth);
                }
                if (stencilFlag) {
                    gl.clearStencil(clearStencil);
                }
                gl.clear(colorFlag | depthFlag | stencilFlag);
            }
            gl._currentFramebuffer?.tagDraw();
            gl._currentFramebuffer?.invalidateMipmaps();
        }
    }
    // factory
    createGPUTimer() {
        return new GPUTimer(this);
    }
    createRenderStateSet() {
        return new WebGLRenderStateSet(this._context);
    }
    createBlendingState() {
        return new WebGLBlendingState();
    }
    createColorState() {
        return new WebGLColorState();
    }
    createRasterizerState() {
        return new WebGLRasterizerState();
    }
    createDepthState() {
        return new WebGLDepthState();
    }
    createStencilState() {
        return new WebGLStencilState();
    }
    createSampler(options) {
        return this._samplerCache.fetchSampler(options);
    }
    createTextureFromMipmapData(data, sRGB, options) {
        if (!data) {
            console.error(`Device.createTextureFromMipmapData() failed: invalid data`);
            return null;
        }
        if (data.isCubemap) {
            const tex = new WebGLTextureCube(this);
            tex.createWithMipmapData(data, sRGB, this.parseTextureOptions(options));
            tex.samplerOptions = options?.samplerOptions ?? null;
            return tex;
        } else if (data.isVolume) {
            const tex = new WebGLTexture3D(this);
            tex.createWithMipmapData(data, this.parseTextureOptions(options));
            tex.samplerOptions = options?.samplerOptions ?? null;
            return tex;
        } else if (data.isArray) {
            const tex = new WebGLTexture2DArray(this);
            tex.createWithMipmapData(data, this.parseTextureOptions(options));
            tex.samplerOptions = options?.samplerOptions ?? null;
            return tex;
        } else {
            const tex = new WebGLTexture2D(this);
            tex.createWithMipmapData(data, sRGB, this.parseTextureOptions(options));
            tex.samplerOptions = options?.samplerOptions ?? null;
            return tex;
        }
    }
    createTexture2D(format, width, height, options) {
        const tex = options?.texture ?? new WebGLTexture2D(this);
        if (!tex.isTexture2D()) {
            console.error('createTexture2D() failed: options.texture must be 2d texture');
            return null;
        }
        tex.createEmpty(format, width, height, this.parseTextureOptions(options));
        tex.samplerOptions = options?.samplerOptions ?? null;
        return tex;
    }
    createTexture2DFromImage(element, sRGB, options) {
        const tex = options?.texture ?? new WebGLTexture2D(this);
        if (!tex.isTexture2D()) {
            console.error('createTexture2DFromImage() failed: options.texture must be 2d texture');
            return null;
        }
        tex.loadFromElement(element, sRGB, this.parseTextureOptions(options));
        tex.samplerOptions = options?.samplerOptions ?? null;
        return tex;
    }
    createTexture2DArray(format, width, height, depth, options) {
        const tex = options?.texture ?? new WebGLTexture2DArray(this);
        if (!tex.isTexture2DArray()) {
            console.error('createTexture2DArray() failed: options.texture must be 2d array texture');
            return null;
        }
        tex.createEmpty(format, width, height, depth, this.parseTextureOptions(options));
        tex.samplerOptions = options?.samplerOptions ?? null;
        return tex;
    }
    createTexture2DArrayFromImages(elements, sRGB, options) {
        if (!elements || elements.length === 0) {
            console.error('createTexture2DArrayFromImages() failed: Invalid image elements');
            return null;
        }
        let width = 0;
        let height = 0;
        for (const element of elements){
            if (width === 0 || height === 0) {
                width = element.width;
                height = element.height;
            } else if (width !== element.width || height !== element.height) {
                console.error('createTexture2DArrayFromImages() failed: Image elements must have the same size');
                return null;
            }
        }
        if (options?.texture && !options.texture.isTexture2DArray()) {
            console.error('createTexture2DArrayFromImages() failed: options.texture must be 2d array texture');
            return null;
        }
        let tex = options?.texture;
        if (tex) {
            if (tex.depth !== elements.length) {
                console.error('createTexture2DArrayFromImages() failed: Layer count of options.texture not match the given image elements');
                return null;
            }
            if (tex.width !== width || tex.height !== height) {
                console.error('createTexture2DArrayFromImages() failed: Size of options.texture not match the given image elements');
                return null;
            }
        } else {
            tex = this.createTexture2DArray(sRGB ? 'rgba8unorm-srgb' : 'rgba8unorm', width, height, elements.length, options);
            if (!tex) {
                console.error('createTexture2DArrayFromImages() failed');
                return null;
            }
            for(let i = 0; i < elements.length; i++){
                tex.updateFromElement(elements[i], 0, 0, i, 0, 0, width, height);
            }
        }
        tex.samplerOptions = options?.samplerOptions ?? null;
        return tex;
    }
    createTexture3D(format, width, height, depth, options) {
        if (!this.isWebGL2) {
            console.error('device does not support 3d texture');
            return null;
        }
        const tex = options?.texture ?? new WebGLTexture3D(this);
        if (!tex.isTexture3D()) {
            console.error('createTexture3D() failed: options.texture must be 3d texture');
            return null;
        }
        tex.createEmpty(format, width, height, depth, this.parseTextureOptions(options));
        tex.samplerOptions = options?.samplerOptions ?? null;
        return tex;
    }
    createCubeTexture(format, size, options) {
        const tex = options?.texture ?? new WebGLTextureCube(this);
        if (!tex.isTextureCube()) {
            console.error('createCubeTexture() failed: options.texture must be cube texture');
            return null;
        }
        tex.createEmpty(format, size, this.parseTextureOptions(options));
        tex.samplerOptions = options?.samplerOptions ?? null;
        return tex;
    }
    createTextureVideo(el, samplerOptions) {
        const tex = new WebGLTextureVideo(this, el);
        tex.samplerOptions = samplerOptions ?? null;
        return tex;
    }
    copyFramebufferToTexture2D(src, index, dst, level) {
        if (!src?.isFramebuffer() || !dst?.isTexture2D()) {
            console.error('copyFramebufferToTexture2D(): invalid framebuffer or texture');
            return;
        }
        if (!Number.isInteger(level) || level < 0 || level >= dst.mipLevelCount) {
            console.error('copyFramebufferToTexture2D(): invalid mipmap level');
            return;
        }
        const tex = src.getColorAttachments()[index];
        if (!tex || !tex.isTexture2D()) {
            console.error('copyFramebufferToTexture2D(): Color attachment is not a 2D texture');
            return;
        }
        const srcLevel = src.getColorAttachmentMipLevel(index);
        const srcWidth = Math.max(tex.width >> srcLevel, 1);
        const srcHeight = Math.max(tex.height >> srcLevel, 1);
        const dstWidth = Math.max(dst.width >> level, 1);
        const dstHeight = Math.max(dst.height >> level, 1);
        if (srcWidth !== dstWidth || srcHeight !== dstHeight) {
            console.error('Source texture and destination texture must have same size');
            return;
        }
        if (tex.format !== dst.format) {
            console.error('copyFramebufferToTexture2D(): Color attachment must have same format with destination texture');
            return;
        }
        if (index > 0 && !this.isWebGL2) {
            console.error('copyFramebufferToTexture2D(): Non-zero color attachment index requires WebGL2 or WebGPU device');
            return;
        }
        this.pushDeviceStates();
        // If src is current framebuffer, force mipmaps to be generated
        this.setFramebuffer(null);
        this.setFramebuffer(src);
        const gl = this._context;
        if (this.isWebGL2) {
            gl.readBuffer(gl.COLOR_ATTACHMENT0 + index);
        }
        this.bindTexture(gl.TEXTURE_2D, 0, dst);
        gl.copyTexSubImage2D(gl.TEXTURE_2D, level, 0, 0, 0, 0, srcWidth, srcHeight);
        this.popDeviceStates();
    }
    copyTexture2D(src, srcLevel, dst, dstLevel) {
        if (!src || !dst) {
            console.error('CopyTexture2D(): invalid texture');
            return;
        }
        if (!Number.isInteger(srcLevel) || srcLevel < 0 || srcLevel >= src.mipLevelCount) {
            console.error('CopyTexture2D(): invalid source mipmap level');
            return;
        }
        const fb = this.createFrameBuffer([
            src
        ], null);
        fb.setColorAttachmentGenerateMipmaps(0, false);
        fb.setColorAttachmentMipLevel(0, srcLevel);
        this.copyFramebufferToTexture2D(fb, 0, dst, dstLevel);
        fb.dispose();
    }
    createGPUProgram(params) {
        if (params.type === 'compute') {
            throw new Error('device does not support compute shader');
        }
        const renderProgramParams = params.params;
        return new WebGLGPUProgram(this, renderProgramParams.vs, renderProgramParams.fs, renderProgramParams.bindGroupLayouts, renderProgramParams.vertexAttributes);
    }
    createBindGroup(layout) {
        return new WebGLBindGroup(this, layout);
    }
    createBuffer(sizeInBytes, options) {
        return new WebGLGPUBuffer(this, this.parseBufferOptions(options), sizeInBytes, !this._isWebGL2);
    }
    copyBuffer(sourceBuffer, destBuffer, srcOffset, dstOffset, bytes) {
        if (!this.isWebGL2) {
            console.error(`copyBuffer() is not supported for current device`);
            return;
        }
        const gl = this._context;
        gl.bindBuffer(gl.COPY_READ_BUFFER, sourceBuffer.object);
        gl.bindBuffer(gl.COPY_WRITE_BUFFER, destBuffer.object);
        gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, srcOffset, dstOffset, bytes);
    }
    createIndexBuffer(data, options) {
        return new WebGLIndexBuffer(this, data, this.parseBufferOptions(options, 'index'));
    }
    createStructuredBuffer(structureType, options, data) {
        return new WebGLStructuredBuffer(this, structureType, this.parseBufferOptions(options), data);
    }
    createVertexLayout(options) {
        return new WebGLVertexLayout(this, options);
    }
    createFrameBuffer(colorAttachments, depthAttachement, options) {
        return new WebGLFrameBuffer(this, colorAttachments, depthAttachement, options);
    }
    setBindGroup(index, bindGroup, bindGroupOffsets) {
        if (bindGroupOffsets && !isWebGL2(this._context)) {
            throw new Error(`setBindGroup(): no dynamic offset buffer support for WebGL1 device`);
        }
        this._currentBindGroups[index] = bindGroup;
        this._currentBindGroupOffsets[index] = bindGroupOffsets || null;
    }
    getBindGroup(index) {
        return [
            this._currentBindGroups[index],
            this._currentBindGroupOffsets[index]
        ];
    }
    // render related
    setViewport(vp) {
        if (vp === null || vp === undefined || !Array.isArray(vp) && vp.default) {
            this._currentViewport = {
                x: 0,
                y: 0,
                width: this.deviceXToScreen(this.drawingBufferWidth),
                height: this.deviceYToScreen(this.drawingBufferHeight),
                default: true
            };
        } else {
            if (Array.isArray(vp)) {
                this._currentViewport = {
                    x: vp[0],
                    y: vp[1],
                    width: vp[2],
                    height: vp[3],
                    default: false
                };
            } else {
                this._currentViewport = Object.assign({
                    default: false
                }, vp);
            }
        }
        this._context.viewport(this.screenXToDevice(this._currentViewport.x), this.screenYToDevice(this._currentViewport.y), this.screenXToDevice(this._currentViewport.width), this.screenYToDevice(this._currentViewport.height));
    }
    getViewport() {
        return this._currentViewport;
    }
    setScissor(scissor) {
        if (scissor === null || scissor === undefined || !Array.isArray(scissor) && scissor.default) {
            this._currentScissorRect = {
                x: 0,
                y: 0,
                width: this.deviceXToScreen(this.drawingBufferWidth),
                height: this.deviceYToScreen(this.drawingBufferHeight),
                default: true
            };
        } else {
            if (Array.isArray(scissor)) {
                this._currentScissorRect = {
                    x: scissor[0],
                    y: scissor[1],
                    width: scissor[2],
                    height: scissor[3],
                    default: false
                };
            } else {
                this._currentScissorRect = Object.assign({
                    default: false
                }, scissor);
            }
        }
        this._context.scissor(this.screenXToDevice(this._currentScissorRect.x), this.screenYToDevice(this._currentScissorRect.y), this.screenXToDevice(this._currentScissorRect.width), this.screenYToDevice(this._currentScissorRect.height));
    }
    getScissor() {
        return this._currentScissorRect;
    }
    setProgram(program) {
        this._currentProgram = program;
    }
    getProgram() {
        return this._currentProgram;
    }
    setVertexLayout(vertexData) {
        this._currentVertexData = vertexData;
    }
    getVertexLayout() {
        return this._currentVertexData;
    }
    setRenderStates(stateSet) {
        this._currentStateSet = stateSet;
    }
    getRenderStates() {
        return this._currentStateSet;
    }
    getFramebuffer() {
        return this._context._currentFramebuffer ?? null;
    }
    reverseVertexWindingOrder(reverse) {
        if (this._reverseWindingOrder !== !!reverse) {
            this._reverseWindingOrder = !!reverse;
            this._context.frontFace(reverse ? this._context.CW : this._context.CCW);
        }
    }
    isWindingOrderReversed() {
        return this._reverseWindingOrder;
    }
    flush() {
        this.context.flush();
    }
    async readPixels(index, x, y, w, h, buffer) {
        const fb = this.getFramebuffer();
        const colorAttachment = fb ? fb.getColorAttachments()[index] : null;
        const format = colorAttachment ? colorAttachment.format : 'rgba8unorm';
        let formatInfo = this._readFormats[format];
        if (!formatInfo) {
            formatInfo = {
                format: this.context.getParameter(WebGLEnum.IMPLEMENTATION_COLOR_READ_FORMAT),
                type: this.context.getParameter(WebGLEnum.IMPLEMENTATION_COLOR_READ_TYPE)
            };
            this._readFormats[format] = formatInfo;
        }
        const glFormat = formatInfo.format;
        const glType = formatInfo.type;
        const pixelSize = getTextureFormatBlockSize(format);
        if ((glFormat !== WebGLEnum.RGBA || glType !== WebGLEnum.UNSIGNED_BYTE && glType !== WebGLEnum.FLOAT && glType !== WebGLEnum.HALF_FLOAT) && !isWebGL2(this.context)) {
            throw new Error(`readPixels() failed: invalid format: ${format}`);
        }
        const byteSize = w * h * pixelSize;
        if (buffer.byteLength < byteSize) {
            throw new Error(`readPixels() failed: destination buffer must have at least ${byteSize} bytes`);
        }
        if (isWebGL2(this.context)) {
            const stagingBuffer = this.createBuffer(byteSize, {
                usage: 'pack-pixel',
                managed: false
            });
            this.context.bindBuffer(WebGLEnum.PIXEL_PACK_BUFFER, stagingBuffer.object);
            this.context.readBuffer(fb ? WebGLEnum.COLOR_ATTACHMENT0 + index : WebGLEnum.COLOR_ATTACHMENT0);
            //this.flush();
            this.context.readPixels(x, y, w, h, glFormat, glType, 0);
            this.context.bindBuffer(WebGLEnum.PIXEL_PACK_BUFFER, null);
            const data = new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
            await stagingBuffer.getBufferSubData(data);
            stagingBuffer.dispose();
        } else {
            this.context.readPixels(x, y, w, h, glFormat, glType, buffer);
        }
    }
    readPixelsToBuffer(index, x, y, w, h, buffer) {
        const fb = this.getFramebuffer();
        const colorAttachment = fb ? fb.getColorAttachments()[index] : null;
        const format = colorAttachment ? colorAttachment.format : 'rgba8unorm';
        let glFormat = WebGLEnum.NONE;
        let glType = WebGLEnum.NONE;
        if (!isWebGL2(this.context)) {
            throw new Error('readPixels() failed: readPixels() requires webgl2 device');
        }
        if (isCompressedTextureFormat(format) || hasDepthChannel(format)) {
            throw new Error(`readPixels() failed: invalid format: ${format}`);
        }
        const r = hasRedChannel(format);
        const g = hasGreenChannel(format);
        const b = hasBlueChannel(format);
        const a = hasAlphaChannel(format);
        const numChannels = (r ? 1 : 0) + (g ? 1 : 0) + (b ? 1 : 0) + (a ? 1 : 0);
        const size = getTextureFormatBlockSize(format) / numChannels;
        const integer = isIntegerTextureFormat(format);
        const float = isFloatTextureFormat(format);
        const signed = isSignedTextureFormat(format);
        if (r && g && b && a) {
            glFormat = integer ? WebGLEnum.RGBA_INTEGER : WebGLEnum.RGBA;
        } else if (r && g) {
            glFormat = integer ? WebGLEnum.RG_INTEGER : WebGLEnum.RG;
        } else if (r) {
            glFormat = integer ? WebGLEnum.RED_INTEGER : WebGLEnum.RED;
        }
        if (size === 1) {
            glType = signed ? WebGLEnum.BYTE : WebGLEnum.UNSIGNED_BYTE;
        } else if (size === 2) {
            glType = float ? WebGLEnum.HALF_FLOAT : signed ? WebGLEnum.SHORT : WebGLEnum.UNSIGNED_SHORT;
        } else if (size === 4) {
            glType = float ? WebGLEnum.FLOAT : signed ? WebGLEnum.INT : WebGLEnum.UNSIGNED_INT;
        }
        this.context.bindBuffer(WebGLEnum.PIXEL_PACK_BUFFER, buffer.object);
        this.context.readBuffer(fb ? WebGLEnum.COLOR_ATTACHMENT0 + index : WebGLEnum.COLOR_ATTACHMENT0);
        this.context.readPixels(x, y, w, h, glFormat, glType, 0);
        this.context.bindBuffer(WebGLEnum.PIXEL_PACK_BUFFER, null);
    }
    looseContext() {
        if (!this.context.isContextLost()) {
            this._loseContextExtension?.loseContext();
        }
    }
    restoreContext() {
        if (this.context.isContextLost()) {
            this.clearErrors();
            this._loseContextExtension?.restoreContext();
            const err = this.getError();
            if (err) {
                console.error(err);
            }
        }
    }
    beginCapture() {
        if (this._captureRenderBundle) {
            throw new Error('Device.beginCapture() failed: device is already capturing draw commands');
        }
        this._captureRenderBundle = [];
    }
    endCapture() {
        if (!this._captureRenderBundle) {
            throw new Error('Device.endCapture() failed: device is not capturing draw commands');
        }
        const result = this._captureRenderBundle;
        this._captureRenderBundle = null;
        return result;
    }
    _executeRenderBundle(renderBundle) {
        for (const drawcall of renderBundle){
            this.setProgram(drawcall.program);
            this.setVertexLayout(drawcall.vertexLayout);
            this.setRenderStates(drawcall.renderStateSet);
            for(let i = 0; i < 4; i++){
                this.setBindGroup(i, drawcall.bindGroups[i], drawcall.bindGroupOffsets[i]);
            }
            if (drawcall.numInstances === 0) {
                this.draw(drawcall.primitiveType, drawcall.first, drawcall.count);
            } else {
                this.drawInstanced(drawcall.primitiveType, drawcall.first, drawcall.count, drawcall.numInstances);
            }
        }
        return renderBundle.length;
    }
    /** @internal */ _setFramebuffer(rt) {
        if (rt !== this._context._currentFramebuffer) {
            this._context._currentFramebuffer?.unbind();
            rt?.bind();
        }
    }
    /** @internal */ onBeginFrame() {
        if (this._contextLost) {
            if (!this._context.isContextLost()) {
                this._contextLost = false;
                this.handleContextRestored();
            }
        }
        return !this._contextLost;
    }
    nextFrame(callback) {
        return requestAnimationFrame(callback);
    }
    cancelNextFrame(handle) {
        cancelAnimationFrame(handle);
    }
    /** @internal */ onEndFrame() {}
    /** @internal */ _draw(primitiveType, first, count) {
        if (this._currentVertexData) {
            this._currentVertexData.bind();
            if (this._currentProgram) {
                if (!this._currentProgram.use()) {
                    return;
                }
                for(let i = 0; i < this._currentProgram.bindGroupLayouts.length; i++){
                    const bindGroup = this._currentBindGroups[i];
                    if (bindGroup) {
                        const offsets = this._currentBindGroupOffsets[i];
                        bindGroup.apply(this._currentProgram, offsets);
                    } else {
                        console.error(`Missing bind group (${i}) when drawing with program '${this._currentProgram.name}'`);
                        return;
                    }
                }
            }
            if (this._currentStateSet) {
                this._currentStateSet.apply();
            } else {
                WebGLRenderStateSet.applyDefaults(this._context);
            }
            const indexBuffer = this._currentVertexData.indexBuffer;
            if (indexBuffer) {
                this.context.drawElements(primitiveTypeMap[primitiveType], count, typeMap[indexBuffer.indexType.primitiveType], first * (indexBuffer.indexType === typeU16 ? 2 : 4));
            } else {
                this.context.drawArrays(primitiveTypeMap[primitiveType], first, count);
            }
            this._context._currentFramebuffer?.tagDraw();
            this.getFramebuffer()?.invalidateMipmaps();
        }
        if (this._captureRenderBundle) {
            const rs = this._currentStateSet?.clone() ?? this.createRenderStateSet();
            if (this._reverseWindingOrder) {
                const rasterState = rs.rasterizerState;
                if (!rasterState) {
                    rs.useRasterizerState().setCullMode('front');
                } else if (rasterState.cullMode === 'back') {
                    rasterState.cullMode = 'front';
                } else if (rasterState.cullMode === 'front') {
                    rasterState.cullMode = 'back';
                }
            }
            this._captureRenderBundle.push({
                bindGroups: [
                    ...this._currentBindGroups
                ],
                bindGroupOffsets: this._currentBindGroupOffsets.map((val)=>val ? [
                        ...val
                    ] : null),
                program: this._currentProgram,
                vertexLayout: this._currentVertexData,
                primitiveType: primitiveType,
                renderStateSet: rs,
                count,
                first,
                numInstances: 0
            });
        }
    }
    /** @internal */ _drawInstanced(primitiveType, first, count, numInstances) {
        if (this.instancedArraysExt && this._currentVertexData) {
            this._currentVertexData.bind();
            if (this._currentProgram) {
                if (!this._currentProgram.use()) {
                    return;
                }
                for(let i = 0; i < this._currentProgram.bindGroupLayouts.length; i++){
                    const bindGroup = this._currentBindGroups[i];
                    if (bindGroup) {
                        const offsets = this._currentBindGroupOffsets[i];
                        bindGroup.apply(this._currentProgram, offsets);
                    } else {
                        console.error(`Missing bind group (${i}) when drawing with program '${this._currentProgram.name}'`);
                        return;
                    }
                }
            }
            this._currentStateSet?.apply();
            const indexBuffer = this._currentVertexData.indexBuffer;
            if (indexBuffer) {
                this.instancedArraysExt.drawElementsInstanced(primitiveTypeMap[primitiveType], count, typeMap[indexBuffer.indexType.primitiveType], first * (indexBuffer.indexType === typeU16 ? 2 : 4), numInstances);
            } else {
                this.instancedArraysExt.drawArraysInstanced(primitiveTypeMap[primitiveType], first, count, numInstances);
            }
            this._context._currentFramebuffer?.tagDraw();
            this._context._currentFramebuffer?.invalidateMipmaps();
        }
        if (this._captureRenderBundle) {
            this._captureRenderBundle.push({
                bindGroups: [
                    ...this._currentBindGroups
                ],
                bindGroupOffsets: this._currentBindGroupOffsets.map((val)=>val ? [
                        ...val
                    ] : null),
                program: this._currentProgram,
                vertexLayout: this._currentVertexData,
                primitiveType: primitiveType,
                renderStateSet: this._currentStateSet?.clone() ?? null,
                count,
                first,
                numInstances
            });
        }
    }
    /** @internal */ _compute() {
        throw new Error('WebGL device does not support compute shader');
    }
    /** @internal */ createInstancedArraysEXT() {
        const gl = this._context;
        if (isWebGL2(gl)) {
            return {
                vertexAttribDivisor: gl.vertexAttribDivisor.bind(gl),
                drawArraysInstanced: gl.drawArraysInstanced.bind(gl),
                drawElementsInstanced: gl.drawElementsInstanced.bind(gl)
            };
        } else {
            const extInstancedArray = gl.getExtension('ANGLE_instanced_arrays');
            return extInstancedArray ? {
                vertexAttribDivisor: extInstancedArray.vertexAttribDivisorANGLE.bind(extInstancedArray),
                drawArraysInstanced: extInstancedArray.drawArraysInstancedANGLE.bind(extInstancedArray),
                drawElementsInstanced: extInstancedArray.drawElementsInstancedANGLE.bind(extInstancedArray)
            } : null;
        }
    }
    /** @internal */ createDrawBuffersEXT() {
        const gl = this._context;
        if (isWebGL2(gl)) {
            return {
                drawBuffers: gl.drawBuffers.bind(gl)
            };
        } else {
            const extDrawBuffers = gl.getExtension('WEBGL_draw_buffers');
            return extDrawBuffers ? {
                drawBuffers: extDrawBuffers.drawBuffersWEBGL.bind(extDrawBuffers)
            } : null;
        }
    }
    /** @internal */ createVertexArrayObjectEXT() {
        const gl = this._context;
        if (isWebGL2(gl)) {
            return {
                createVertexArray: gl.createVertexArray.bind(gl),
                bindVertexArray: gl.bindVertexArray.bind(gl),
                deleteVertexArray: gl.deleteVertexArray.bind(gl),
                isVertexArray: gl.isVertexArray.bind(gl)
            };
        } else {
            const extVAO = gl.getExtension('OES_vertex_array_object');
            return extVAO ? {
                createVertexArray: extVAO.createVertexArrayOES.bind(extVAO),
                bindVertexArray: extVAO.bindVertexArrayOES.bind(extVAO),
                deleteVertexArray: extVAO.deleteVertexArrayOES.bind(extVAO),
                isVertexArray: extVAO.isVertexArrayOES.bind(extVAO)
            } : null;
        }
    }
    /** @internal */ handleContextLost() {
        this._isRendering = this.isRendering;
        this.invalidateAll();
        this.dispatchEvent('devicelost');
    }
    /** @internal */ handleContextRestored() {
        console.info('handle context restored');
        this.initContextState();
        this._textureSamplerMap = new WeakMap();
        this._currentProgram = null;
        this._currentVertexData = null;
        this._currentStateSet = null;
        this._currentBindGroups = [];
        this._currentBindGroupOffsets = [];
        this._currentViewport = {
            x: 0,
            y: 0,
            width: this.deviceXToScreen(this.canvas.width),
            height: this.deviceYToScreen(this.canvas.height),
            default: true
        };
        this._currentScissorRect = {
            x: 0,
            y: 0,
            width: this.deviceXToScreen(this.canvas.width),
            height: this.deviceYToScreen(this.canvas.height),
            default: true
        };
        this._samplerCache = new SamplerCache(this);
        if (this._isRendering) {
            this._isRendering = false;
            this.reloadAll();
            this.dispatchEvent('devicerestored');
        }
    }
    /** @internal */ initContextState() {
        this._deviceCaps = {
            miscCaps: new WebGLMiscCaps(this._context),
            framebufferCaps: new WebGLFramebufferCaps(this._context),
            shaderCaps: new WebGLShaderCaps(this._context),
            textureCaps: new WebGLTextureCaps(this._context)
        };
        this._vaoExt = this.createVertexArrayObjectEXT();
        this._instancedArraysExt = this.createInstancedArraysEXT();
        this._drawBuffersExt = this.createDrawBuffersEXT();
        this._context.pixelStorei(WebGLEnum.UNPACK_COLORSPACE_CONVERSION_WEBGL, WebGLEnum.NONE);
        this._context.pixelStorei(WebGLEnum.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        this.setViewport(null);
        this.setScissor(null);
        this._context.enable(WebGLEnum.SCISSOR_TEST);
        this.enableGPUTimeRecording(true);
        this._context._currentFramebuffer = undefined;
        this._context._currentProgram = undefined;
        this._deviceUniformBuffers = [];
        this._deviceUniformBufferOffsets = [];
        this._bindTextures = {
            [WebGLEnum.TEXTURE_2D]: [],
            [WebGLEnum.TEXTURE_CUBE_MAP]: [],
            [WebGLEnum.TEXTURE_3D]: [],
            [WebGLEnum.TEXTURE_2D_ARRAY]: []
        };
        this._bindSamplers = [];
    }
    /** @internal */ clearErrors() {
        while(this._context.getError()){
        // eat errors
        }
    }
    /** @internal */ getCurrentSamplerForTexture(tex) {
        return this._textureSamplerMap.get(tex);
    }
    /** @internal */ setCurrentSamplerForTexture(tex, sampler) {
        this._textureSamplerMap.set(tex, sampler);
    }
    getError(throwError) {
        const errcode = this._context.getError();
        const err = errcode === WebGLEnum.NO_ERROR ? null : new WebGLError(errcode);
        if (err && throwError) {
            throw err;
        }
        return err;
    }
}
let webGL1Supported = null;
let webGL2Supported = null;
const factory = makeObservable(WebGLDevice)();
async function createWebGLDevice(backend, cvs, options) {
    try {
        const device = new factory(backend, cvs, options);
        await device.initContext();
        device.setViewport(null);
        device.setScissor(null);
        return device;
    } catch (err) {
        console.error(err);
        return null;
    }
}
/** @internal */ const backend1 = {
    typeName () {
        return 'webgl';
    },
    async supported () {
        if (webGL1Supported === null) {
            const cvs = document.createElement('canvas');
            const gl = cvs.getContext('webgl');
            webGL1Supported = !!gl;
            cvs.width = 0;
            cvs.height = 0;
        }
        return webGL1Supported;
    },
    async createDevice (cvs, options) {
        return createWebGLDevice(this, cvs, options);
    }
};
/** @internal */ const backend2 = {
    typeName () {
        return 'webgl2';
    },
    async supported () {
        if (webGL2Supported === null) {
            const cvs = document.createElement('canvas');
            const gl = cvs.getContext('webgl2');
            webGL2Supported = !!gl;
            cvs.width = 0;
            cvs.height = 0;
        }
        return webGL2Supported;
    },
    async createDevice (cvs, options) {
        return createWebGLDevice(this, cvs, options);
    }
};

export { WebGLDevice, backend1, backend2 };
//# sourceMappingURL=device_webgl.js.map
